import type { ReactNode } from "react";
import { PermLink } from "./PermLink";
import { useTranslation } from "react-i18next";
import { REPLACEMENT_KIND_LABEL, type AssetSummary } from "../api/assets";
import { ConditionChip, UsageChip } from "./ConditionChip";
import { formatDate, isDueDate } from "../lib/dates";
import { shortPathLabel } from "../lib/paths";

/** "Privremena zamena — podsetnik 21.09." — amber, red once the reminder date has passed. */
/**
 * "Privremena zamena — podsetnik 25.09." pill. `compact` (dense tables, tablet width —
 * Strahinja, 2026-09-23) shortens it to "Privremena · 25.09." and keeps the full text in the tooltip.
 */
export function ReplacementBadge({ asset, compact = false }: {
  asset: Pick<AssetSummary, "replacementKind" | "replacementReminderAt" | "usage">;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  if (asset.usage !== "MONTIRAN" || asset.replacementKind !== "TEMPORARY") return null;
  const due = isDueDate(asset.replacementReminderAt);
  const date = formatDate(asset.replacementReminderAt);
  const full = date
    ? t("assets.reminder.badge", "Privremena zamena — podsetnik {{date}}", { date: date.slice(0, 6) })
    : t("assets.reminder.badgeNoDate", "Privremena zamena");
  const label = compact && date ? t("assets.reminder.badgeShort", "Privremena · {{date}}", { date: date.slice(0, 6) }) : full;
  const dueTitle = due ? t("assets.reminder.dueTitle", "Podsetnik je dospeo") : null;
  const title = compact ? [dueTitle, full].filter(Boolean).join(" — ") : dueTitle ?? undefined;
  return (
    <span className={`tag ${due ? "err" : "warn"} dot`} title={title || undefined}>
      {label}
    </span>
  );
}

/**
 * Compact asset card: tag (link to the karton), chips, type, where it is.
 * Used by the structure page ("Montiran asset") and the QR/label screens.
 */
export function AssetSummaryCard({ asset, actions, showPosition = true }: { asset: AssetSummary; actions?: ReactNode; showPosition?: boolean }) {
  const { t } = useTranslation();
  const pos = asset.positionId ? shortPathLabel(asset.positionPath, asset.positionName) : null;
  return (
    <div className="asset-summary">
      <div className="asset-summary-head">
        <PermLink permission="assets:view" to={`/asseti/${asset.id}`} className="asset-tag">{asset.tag}</PermLink>
        <ConditionChip condition={asset.condition} />
        <UsageChip usage={asset.usage} />
        <ReplacementBadge asset={asset} />
        {asset.replacementKind === "PERMANENT" && asset.usage === "MONTIRAN" && (
          <span className="tag">{t("assets.replacementKind.PERMANENT", REPLACEMENT_KIND_LABEL.PERMANENT)}</span>
        )}
      </div>
      <dl className="kv">
        <dt>{t("assets.field.type", "Tip")}</dt>
        <dd>
          <PermLink permission="types:view" to={`/tipovi/${asset.typeId}`}>
            <span className="mono" style={{ fontSize: 12.5 }}>{asset.typeCode}</span> {asset.typeName}
          </PermLink>
        </dd>
        {(asset.manufacturer || asset.isCustomMade) && (
          <>
            <dt>{t("assets.field.manufacturer", "Proizvođač")}</dt>
            <dd>
              {asset.manufacturer}
              {asset.manufacturerCode && <span className="muted"> · {asset.manufacturerCode}</span>}
              {asset.isCustomMade && <span className="tag info" style={{ marginLeft: asset.manufacturer ? 8 : 0 }}>{t("assets.customMadeTag", "sopstvena izrada")}</span>}
            </dd>
          </>
        )}
        <dt>{t("assets.field.serialNo", "Serijski broj")}</dt>
        <dd className="mono">{asset.serialNo ?? <span className="muted">—</span>}</dd>
        {showPosition && pos && (
          <>
            <dt>{t("assets.field.position", "Pozicija")}</dt>
            <dd><PermLink permission="structure:view" to={`/struktura/${asset.positionId}`} title={pos.full}>{pos.short}</PermLink></dd>
          </>
        )}
        <dt>{t("assets.field.locationEffective", "Fizička lokacija")}</dt>
        <dd>
          {asset.effectiveLocation
            ? <>{asset.effectiveLocation.name} <span className="muted mono" style={{ fontSize: 12 }}>{asset.effectiveLocation.code}</span></>
            : <span className="muted">—</span>}
          {asset.locationNote && <span className="muted"> · {asset.locationNote}</span>}
        </dd>
        {asset.expectedReturnAt && (
          <>
            <dt>{t("assets.field.expectedReturnAt", "Očekivani povratak sa servisa")}</dt>
            <dd>{formatDate(asset.expectedReturnAt)}</dd>
          </>
        )}
      </dl>
      {actions && <div className="form-actions" style={{ justifyContent: "flex-start" }}>{actions}</div>}
    </div>
  );
}
